import React, { useMemo } from 'react';
import ThinkingBlock from './ThinkingBlock';
import StreamingMarkdown from './StreamingMarkdown';
import CollapsibleCode from './CollapsibleCode';

interface StableStreamingMessageProps {
  content: string;
  isStreaming: boolean;
}

interface ContentPart {
  type: 'thinking' | 'code' | 'text';
  content: string;
  language?: string;
}

const StableStreamingMessage: React.FC<StableStreamingMessageProps> = ({ content, isStreaming }) => {
  // Memoize the parsed content to avoid re-parsing on every render
  const parts = useMemo(() => {
    const result: ContentPart[] = [];
    
    // First, unescape HTML entities that might have been escaped by JSON
    let remaining = content
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
    
    let position = 0;
    
    // Debug logging
    if (remaining.includes('<thinking>') || content.includes('&lt;thinking&gt;')) {
      console.log('[StableStreamingMessage] Thinking tag detection:', {
        originalHasEscaped: content.includes('&lt;thinking&gt;'),
        unescapedHasTag: remaining.includes('<thinking>'),
        contentLength: content.length,
        hasClosingTag: remaining.includes('</thinking>'),
        isStreaming
      });
    }
    
    while (remaining.length > 0) {
      // Check for thinking block
      const thinkingStart = remaining.indexOf('<thinking>');
      const codeStart = remaining.search(/```(\w+)?/);
      
      // Find what comes first
      let nextSpecial = -1;
      let specialType: 'thinking' | 'code' | null = null;
      
      if (thinkingStart !== -1 && (codeStart === -1 || thinkingStart < codeStart)) {
        nextSpecial = thinkingStart;
        specialType = 'thinking';
      } else if (codeStart !== -1) {
        nextSpecial = codeStart;
        specialType = 'code';
      }
      
      // Add any text before special content
      if (nextSpecial > 0 || (nextSpecial === -1 && remaining.length > 0)) {
        const textContent = nextSpecial === -1 ? remaining : remaining.substring(0, nextSpecial);
        if (textContent.trim()) {
          result.push({ type: 'text', content: textContent });
        }
        if (nextSpecial === -1) break;
        remaining = remaining.substring(nextSpecial);
      }
      
      // Handle special content
      if (specialType === 'thinking') {
        const endTag = '</thinking>';
        const endIndex = remaining.indexOf(endTag);
        
        if (endIndex !== -1) {
          // Complete thinking block
          const thinkingContent = remaining.substring('<thinking>'.length, endIndex);
          result.push({ type: 'thinking', content: thinkingContent });
          remaining = remaining.substring(endIndex + endTag.length);
        } else if (isStreaming) {
          // Incomplete thinking block during streaming
          const thinkingContent = remaining.substring('<thinking>'.length);
          result.push({ type: 'thinking', content: thinkingContent });
          break;
        } else {
          // Not streaming but no closing tag - treat as text
          result.push({ type: 'text', content: remaining });
          break;
        }
      } else if (specialType === 'code') {
        const codeMatch = remaining.match(/^```(\w+)?\n?/);
        if (codeMatch) {
          const language = codeMatch[1] || 'text';
          const codeStartIndex = codeMatch[0].length;
          const codeEndIndex = remaining.indexOf('```', codeStartIndex);
          
          if (codeEndIndex !== -1) {
            // Complete code block
            const codeContent = remaining.substring(codeStartIndex, codeEndIndex);
            result.push({ type: 'code', content: codeContent, language });
            remaining = remaining.substring(codeEndIndex + 3);
          } else if (isStreaming) {
            // Incomplete code block during streaming
            const codeContent = remaining.substring(codeStartIndex);
            result.push({ type: 'code', content: codeContent, language });
            break;
          } else {
            // Not streaming but no closing - treat as text
            result.push({ type: 'text', content: remaining });
            break;
          }
        }
      }
    }
    
    return result;
  }, [content, isStreaming]);
  
  // Use stable keys based on content position, not array index
  const getStableKey = (index: number, part: ContentPart) => {
    // Create a stable key based on the type and the first 20 chars of content
    const contentPreview = part.content.substring(0, 20).replace(/\s/g, '_');
    return `${part.type}-${index}-${contentPreview}`;
  };
  
  return (
    <div className="stable-streaming-message">
      {parts.map((part, index) => {
        const key = getStableKey(index, part);
        
        switch (part.type) {
          case 'thinking':
            return (
              <ThinkingBlock
                key={key}
                content={part.content}
                isStreaming={isStreaming && index === parts.length - 1}
              />
            );
            
          case 'code':
            if (isStreaming && index === parts.length - 1) {
              // Streaming code block
              return (
                <div key={key} className="streaming-code-block">
                  <div className="code-header">
                    <span className="code-language">{part.language?.toUpperCase() || 'CODE'}</span>
                    <span className="streaming-indicator">Generating...</span>
                  </div>
                  <pre className="code-block">
                    <code>{part.content}</code>
                  </pre>
                </div>
              );
            } else {
              // Complete code block
              return (
                <CollapsibleCode
                  key={key}
                  code={part.content}
                  language={part.language || 'text'}
                />
              );
            }
            
          case 'text':
            return (
              <StreamingMarkdown
                key={key}
                content={part.content}
                isStreaming={isStreaming && index === parts.length - 1}
              />
            );
        }
      })}
    </div>
  );
};

export default StableStreamingMessage;